import { useNavigate, useParams } from "react-router-dom";
import {
  TextField,
  Button,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  FormControlLabel,
  CircularProgress
} from "@mui/material";
import { useState, useEffect } from "react";
import { useCreateRoleMutation, useUpdateRoleMutation, useGetRoleDetailQuery, Permission } from "../../store/api/manageAPI";
import { showToast, showError } from "../../constants/toasts";

const AddRole = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [createRole, { isLoading: isCreating }] = useCreateRoleMutation();
  const [updateRole, { isLoading: isUpdating }] = useUpdateRoleMutation();
  const { data: roleData, isLoading: isFetching } = useGetRoleDetailQuery(
    { id: id || "" },
    { skip: !isEditMode }
  );

  const [roleName, setRoleName] = useState("");

  const modules = [
    { id: 1, name: "Dashboard", path: "/dashboard" },
    { id: 2, name: "User Management", path: "/manage-users" },
    { id: 3, name: "Manage Dispatchers", path: "/manage-dispatcher" },
    { id: 4, name: "Manage Stores", path: "/manage-stores" },
    { id: 5, name: "Order Management", path: "/manage-order" },
    { id: 6, name: "Mange Yogurt Types", path: "/manage-yogurt-types" },
    { id: 7, name: "Manage Toppings", path: "/manage-toppings" },
    { id: 8, name: "Mange Flavors", path: "/manage-flavour" },
    { id: 9, name: "Manage Product Categories", path: "/manage-product-categories" },
    { id: 10, name: "Mange Products", path: "/manage-products" },
    { id: 11, name: "Manage Box Art", path: "/manage-box-art" },
    { id: 12, name: "Manage Greek Box", path: "/manage-greek-box" },
    { id: 13, name: "Manage loyalty points", path: "/manage-loyality-points" },
    { id: 14, name: "Notification Management", path: "/manage-notifications" },
    { id: 15, name: "Revenue Generation", path: "/manage-transactions" },
    { id: 16, name: "Report Analytics", path: "/revenue-analytic" },
    { id: 17, name: "Manage Ratings & Reviews", path: "/manage-ratings" },
    { id: 18, name: "Manage CMS Pages", path: "/manage-cms" },
    { id: 19, name: "Manage Customer Support", path: "/customer-support" },
  ];

  const [permissions, setPermissions] = useState<Permission[]>(
    modules.map((module) => ({
      sideBarId: module.id,
      label: module.name,
      path: module.path,
      isView: false,
      isAdd: false,
      isEdit: false,
      isDelete: false
    }))
  );

  useEffect(() => {
    if (isEditMode && roleData?.data) {
      const role = roleData.data;
      setRoleName(role.name);

      // Update permissions based on fetched data
      if (role.permissions) {
        setPermissions(prevPermissions => {
          return prevPermissions.map(defaultPerm => {
            const foundPerm = role.permissions.find((p: Permission) => p.sideBarId === defaultPerm.sideBarId);
            if (foundPerm) {
              return {
                ...defaultPerm,
                isView: foundPerm.isView,
                isAdd: foundPerm.isAdd,
                isEdit: foundPerm.isEdit,
                isDelete: foundPerm.isDelete,
              };
            }
            return defaultPerm;
          });
        });
      }
    }
  }, [isEditMode, roleData]);

  const handlePermissionChange = (sideBarId: number, field: string) => {
    setPermissions(prev =>
      prev.map(row =>
        row.sideBarId === sideBarId ? { ...row, [field]: !row[field as keyof typeof row] } : row
      )
    );
  };

  const handleSave = async () => {
    if (!roleName.trim()) {
      showError("Please enter a role name");
      return;
    }

    try {
      let response;
      if (isEditMode && id) {
        response = await updateRole({
          id,
          name: roleName,
          permissions: permissions
        }).unwrap();
      } else {
        response = await createRole({
          name: roleName,
          permissions: permissions
        }).unwrap();
      }

      // Check for success based on response structure
      if (response && (response.success || response.message)) {
        showToast(response.message || `Role ${isEditMode ? 'updated' : 'created'} successfully`);
        navigate("/manage-subadmin/roles");
      }
    } catch (error: any) {
      showError(error?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} role`);
    }
  };

  const isLoading = isCreating || isUpdating || isFetching;

  if (isFetching) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "50px" }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <div className="main_title">
        <h1>{isEditMode ? "Edit Role" : "Add Role"}</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button>
          {" "} - <button onClick={() => navigate("/manage-subadmin")}>Manage Sub-Admin</button>
          {" "} - <button onClick={() => navigate("/manage-subadmin/roles")}>Manage Roles</button>
          {" "} - {isEditMode ? "Edit Role" : "Add Role"}
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <div className="gap_p">
            <div className="control_group w_50">
              <label htmlFor="role">Role Name</label>
              <TextField
                id="role"
                placeholder="Enter Role Name"
                hiddenLabel
                fullWidth
                value={roleName}
                onChange={(e) => {
                  if (e.target.value.length <= 40) {
                    setRoleName(e.target.value);
                  }
                }}
                inputProps={{ maxLength: 40 }}
              />
            </div>
          </div>

          <TableContainer className="table_container mt_20">
            <Table>
              <TableHead>
                <TableRow hover>
                  <TableCell>Module Name</TableCell>
                  <TableCell align="center">View</TableCell>
                  <TableCell align="center">Add</TableCell>
                  <TableCell align="center">Edit</TableCell>
                  <TableCell align="center">Delete</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {permissions.map(row => (
                  <TableRow hover key={row.sideBarId}>
                    <TableCell>
                      <strong>{row.label}</strong>
                    </TableCell>
                    {["isView", "isAdd", "isEdit", "isDelete"].map(action => (
                      <TableCell align="center" key={action}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(row[action as keyof typeof row])}
                              onChange={() => handlePermissionChange(row.sideBarId, action)}
                            />
                          }
                          name="check"
                          label=""
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <div className="form_btn">
            <Button
              className="btn btn_primary"
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : "Save"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddRole;
